import { useState } from "react";
import easyTaxesFooterLogo from "@/assets/easy-taxes-footer-logo.png?format=webp";
import { Link } from "react-router-dom";
import { Mail, MapPin, Clock, Instagram, Linkedin, Facebook, Lock, Cookie } from "lucide-react";
import { CookieConsent } from "@/components/CookieConsent";

export const Footer = () => {
  const [showCookieSettings, setShowCookieSettings] = useState(false);

  return (
    <>
      {showCookieSettings && (
        <CookieConsent 
          forceOpen={true} 
          onClose={() => setShowCookieSettings(false)} 
        />
      )}
    <footer className="py-8 md:py-10 bg-green-primary" role="contentinfo" aria-label="Pied de page Easy Taxes">
      <div className="container-custom px-4">
        {/* 3 colonnes principales */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 lg:gap-10">
          
          {/* Colonne 1 : Contact */}
          <div className="space-y-4" aria-labelledby="footer-contact-title">
            <h3 id="footer-contact-title" className="text-white font-medium text-sm uppercase tracking-widest mb-5">
              Contact
            </h3>
            
            <a 
              href="mailto:contact@elyx-finance.ch" 
              className="flex items-center gap-3 text-white/80 hover:text-gold-primary transition-colors text-sm group focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-primary focus-visible:ring-offset-2 focus-visible:ring-offset-green-primary rounded-sm"
              aria-label="Envoyer un email à contact@elyx-finance.ch"
            >
              <Mail className="w-4 h-4 flex-shrink-0" strokeWidth={1.5} aria-hidden="true" />
              <span>contact@elyx-finance.ch</span>
            </a>
            
            <address className="flex items-start gap-3 text-white/80 text-sm not-italic" aria-label="Adresse postale ELYX Finance">
              <MapPin className="w-4 h-4 flex-shrink-0 mt-0.5" strokeWidth={1.5} aria-hidden="true" />
              <div>
                <p>Chemin du Pavillon 2</p>
                <p>1218 Le Grand-Saconnex</p>
                <p>Genève, Suisse</p>
              </div>
            </address>
            
            <div className="flex items-center gap-3 text-white/80 text-sm" aria-label="Horaires d'ouverture">
              <Clock className="w-4 h-4 flex-shrink-0" strokeWidth={1.5} aria-hidden="true" />
              <span>Lun–Ven : 9h00–18h00</span>
            </div>
            
            {/* Réseaux sociaux - LinkedIn prioritaire */}
            <nav className="flex items-center gap-4 pt-2" aria-label="Réseaux sociaux">
              <a 
                href="https://www.linkedin.com/company/elyx-finance/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-white/80 hover:text-white hover:scale-110 transition-all duration-300 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-primary focus-visible:ring-offset-2 focus-visible:ring-offset-green-primary rounded-sm"
                aria-label="LinkedIn ELYX Finance (ouvre dans un nouvel onglet)"
              >
                <Linkedin className="w-5 h-5" strokeWidth={1.5} aria-hidden="true" />
              </a>
              <a 
                href="https://www.instagram.com/elyx_finance?igsh=amx0N3M1bDdncXNu&utm_source=qr"
                target="_blank"
                rel="noopener noreferrer"
                className="text-white/80 hover:text-white hover:scale-110 transition-all duration-300 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-primary focus-visible:ring-offset-2 focus-visible:ring-offset-green-primary rounded-sm"
                aria-label="Instagram ELYX Finance (ouvre dans un nouvel onglet)"
              >
                <Instagram className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" />
              </a>
              <a 
                href="https://www.facebook.com/share/1BAds3cv1c/?mibextid=wwXIfr"
                target="_blank"
                rel="noopener noreferrer"
                className="text-white/80 hover:text-white hover:scale-110 transition-all duration-300 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-primary focus-visible:ring-offset-2 focus-visible:ring-offset-green-primary rounded-sm"
                aria-label="Facebook ELYX Finance (ouvre dans un nouvel onglet)"
              >
                <Facebook className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" />
              </a>
            </nav>
          </div>
          
          {/* Colonne 2 : Liens */}
          <div className="space-y-4" aria-labelledby="footer-links-title">
            <h3 id="footer-links-title" className="text-white font-medium text-sm uppercase tracking-widest mb-5">
              Liens
            </h3>
            
            <nav className="flex flex-col space-y-3" aria-label="Navigation du site">
              <Link
                to="/declaration-impots-resident" 
                className="text-white/80 hover:text-gold-primary transition-colors text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-primary focus-visible:ring-offset-2 focus-visible:ring-offset-green-primary rounded-sm"
                aria-label="Déclaration d'impôts pour résidents suisses et permis C"
              >
                Déclaration d'impôts résidents
              </Link>
              <Link 
                to="/declaration-impots-permis-b" 
                className="text-white/80 hover:text-gold-primary transition-colors text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-primary focus-visible:ring-offset-2 focus-visible:ring-offset-green-primary rounded-sm"
                aria-label="Déclaration d'impôts pour titulaires d'un permis B"
              >
                Déclaration d'impôts permis B
              </Link>
              <Link
                to="/declaration-impots-frontalier" 
                className="text-white/80 hover:text-gold-primary transition-colors text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-primary focus-visible:ring-offset-2 focus-visible:ring-offset-green-primary rounded-sm"
                aria-label="Déclaration d'impôts pour frontaliers"
              >
                Déclaration d'impôts frontaliers
              </Link>
              <Link 
                to="/3eme-pilier" 
                className="text-white/80 hover:text-gold-primary transition-colors text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-primary focus-visible:ring-offset-2 focus-visible:ring-offset-green-primary rounded-sm"
                aria-label="Guide du 3e pilier en Suisse"
              >
                Guide 3e pilier
              </Link>
              <Link 
                to="/assurance-maladie-impots" 
                className="text-white/80 hover:text-gold-primary transition-colors text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-primary focus-visible:ring-offset-2 focus-visible:ring-offset-green-primary rounded-sm"
                aria-label="Guide assurance maladie et impôts"
              >
                Guide assurance maladie
              </Link>
              <Link 
                to="/parrainage"
                className="text-white/80 hover:text-gold-primary transition-colors text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-primary focus-visible:ring-offset-2 focus-visible:ring-offset-green-primary rounded-sm"
                aria-label="En savoir plus sur le programme de parrainage"
              >
                Programme de parrainage
              </Link>
              <Link 
                to="/blog" 
                className="text-white/80 hover:text-gold-primary transition-colors text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-primary focus-visible:ring-offset-2 focus-visible:ring-offset-green-primary rounded-sm"
                aria-label="Accéder au blog Easy Taxes"
              >
                Blog
              </Link>
            </nav>
          </div>
          
          {/* Colonne 3 : Plateforme sécurisée */}
          <div className="space-y-4" aria-labelledby="footer-client-title">
            <h3 id="footer-client-title" className="text-white font-medium text-sm uppercase tracking-widest mb-5">
              Votre espace client
            </h3>
            
            <a 
              href="https://taxes.elyx-finance.ch/auth?tab=signup"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-4 py-2.5 border border-white/30 rounded-lg text-white/90 text-sm font-medium hover:bg-white/10 hover:border-white/50 hover:shadow-[0_0_16px_rgba(255,255,255,0.15)] transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-primary focus-visible:ring-offset-2 focus-visible:ring-offset-green-primary"
              aria-label="Accéder à votre espace client sécurisé Easy Taxes (ouvre dans un nouvel onglet)"
            >
              <Lock className="w-4 h-4" strokeWidth={1.5} aria-hidden="true" />
              <span>Accéder à votre espace client</span>
            </a>
            
            <p className="text-white/80 text-sm leading-relaxed">
              Création de compte, dépôt de documents, messagerie sécurisée et suivi de dossier fiscal.
            </p>
            
            <Link 
              to="/politique-de-confidentialite" 
              className="text-white/80 hover:text-gold-primary transition-colors text-xs focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-primary focus-visible:ring-offset-2 focus-visible:ring-offset-green-primary rounded-sm"
              aria-label="Consulter les mentions légales et politique de confidentialité"
            >
              Mentions légales & Confidentialité
            </Link>
            
            <button
              onClick={() => setShowCookieSettings(true)}
              className="flex items-center gap-1.5 text-white/80 hover:text-gold-primary transition-colors text-xs focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-primary focus-visible:ring-offset-2 focus-visible:ring-offset-green-primary rounded-sm mt-2"
              aria-label="Gérer vos préférences de cookies"
            >
              <Cookie className="w-3 h-3" aria-hidden="true" />
              Gérer les cookies
            </button>
          </div>
        </div>

        {/* Séparateur */}
        <hr className="border-t border-white/10 my-6 md:my-8" role="separator" aria-hidden="true" />

        {/* Barre inférieure */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4" role="contentinfo">
          {/* Logo */}
          <div className="flex justify-center md:justify-start">
            <button 
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              aria-label="Retour en haut de la page"
              className="focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-primary focus-visible:ring-offset-2 focus-visible:ring-offset-green-primary rounded-lg"
            >
              <img 
                src={easyTaxesFooterLogo} 
                alt="Easy Taxes par Elyx Finance" 
                width={140}
                height={49}
                loading="lazy"
                decoding="async"
                className="h-10 md:h-12 w-auto cursor-pointer"
              />
            </button>
          </div>
          
          {/* Mentions légales centrales */}
          <div className="text-center space-y-1.5">
            <p className="text-white/80 text-[11px] md:text-xs">
              Easy-Taxes.ch est un service proposé par ELYX Finance SNC
            </p>
            <p className="text-white/80 text-[10px] md:text-[11px]">
              ELYX Finance SNC – Intermédiaire d'assurance non lié inscrit au registre FINMA (F01517549)
            </p>
          </div>
          
          {/* Copyright + Admin */}
          <div className="flex flex-col items-center md:items-end gap-1.5">
            <p className="text-white/80 text-[10px] md:text-[11px]">
              © {new Date().getFullYear()} Easy Taxes par ELYX Finance SNC
            </p>
            <div className="flex items-center gap-3">
              <p className="text-white/80 text-[9px] md:text-[10px]">
                Tous droits réservés
              </p>
              <a 
                href="https://taxes.elyx-finance.ch/admin-login"
                target="_blank"
                rel="noopener noreferrer"
                className="text-white/80 hover:text-white text-[9px] md:text-[10px] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-primary focus-visible:ring-offset-2 focus-visible:ring-offset-green-primary rounded-sm"
                aria-label="Accès administration (ouvre dans un nouvel onglet)"
              >
                Admin
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
    </>
  );
};
